import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Iframe, getModalDimensions } from '../../custom-ui';
import Modal, { ModalTransition } from '@atlaskit/modal-dialog';
import { useIsReducedMotion } from '@atlaskit/motion/accessibility';
import { getAtlasKitModalWidth, ModalContentWrapper } from './custom-ui-modal-utils';
import { ForgeDeprecationMessage, SANDBOX_DEPRECATION_ERROR } from '../ForgeDeprecationMessage';
export var CustomUIRenderer = function CustomUIRenderer(_ref) {
  var accountId = _ref.accountId,
    bridge = _ref.bridge,
    client = _ref.client,
    contextIds = _ref.contextIds,
    coreData = _ref.coreData,
    customBridgeMethods = _ref.customBridgeMethods,
    dialogs = _ref.dialogs,
    entryPoint = _ref.entryPoint,
    extension = _ref.extension,
    extensionData = _ref.extensionData,
    extensionPayload = _ref.extensionPayload,
    extensionViewData = _ref.extensionViewData,
    getContextToken = _ref.getContextToken,
    getThreeLOPrompt = _ref.getThreeLOPrompt,
    height = _ref.height,
    width = _ref.width,
    isIframeHidden = _ref.isIframeHidden,
    isIframeResizable = _ref.isIframeResizable,
    iframeRef = _ref.iframeRef,
    isInModal = _ref.isInModal,
    loadingComponent = _ref.loadingComponent,
    locale = _ref.locale,
    localId = _ref.localId,
    modalStyles = _ref.modalStyles,
    modalProvider = _ref.modalProvider,
    onIframeLoad = _ref.onIframeLoad,
    onModalCustomUIRendererClose = _ref.onModalCustomUIRendererClose,
    product = _ref.product,
    timezone = _ref.timezone,
    getModalWidth = _ref.getModalWidth,
    setIsLoading = _ref.setIsLoading;
  var _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    error = _useState2[0],
    setError = _useState2[1];
  // modalOptions is null when no modal is open is non null when opened
  var _useState3 = useState(null),
    _useState4 = _slicedToArray(_useState3, 2),
    modalOptions = _useState4[0],
    setModalOptions = _useState4[1];
  var onCloseFromApp = modalOptions === null || modalOptions === void 0 ? void 0 : modalOptions.onClose;
  var extensionInModal = useMemo(function () {
    var _modalOptions$resourc;
    return _objectSpread(_objectSpread({}, extension), {}, {
      // Pass a type that is not included in the dimensions presets so that it falls back to default
      type: "".concat(extension.type, "::dynamic-modal"),
      properties: _objectSpread(_objectSpread({}, extension.properties), {}, {
        resource: (_modalOptions$resourc = modalOptions === null || modalOptions === void 0 ? void 0 : modalOptions.resource) !== null && _modalOptions$resourc !== void 0 ? _modalOptions$resourc : extension.properties.resource
      })
    });
  }, [extension, modalOptions === null || modalOptions === void 0 ? void 0 : modalOptions.resource]);
  var onError = function onError(error) {
    error && setError(error);
  };
  var extensionDataInModal = useMemo(function () {
    return _objectSpread(_objectSpread(_objectSpread({}, extensionData), modalOptions !== null && modalOptions !== void 0 && modalOptions.context ? {
      modal: modalOptions === null || modalOptions === void 0 ? void 0 : modalOptions.context
    } : {}), (product === 'compass' || extension.type.startsWith('jiraServiceManagement')) && modalOptions !== null && modalOptions !== void 0 && modalOptions.context ? _objectSpread({}, modalOptions === null || modalOptions === void 0 ? void 0 : modalOptions.context) : {});
  }, [extension.type, extensionData, modalOptions === null || modalOptions === void 0 ? void 0 : modalOptions.context, product]);
  var handleModalTearDown = useCallback(function () {
    setModalOptions(null);
  }, [setModalOptions]);
  var isReducedMotion = useIsReducedMotion();
  var handleModalClose = useCallback(function () {
    onCloseFromApp === null || onCloseFromApp === void 0 || onCloseFromApp();
    if (isReducedMotion) {
      handleModalTearDown();
    }
  }, [onCloseFromApp, isReducedMotion, handleModalTearDown]);

  // Confluence specific feature
  // Needed to show Forge modals in live pages. dialogs won't be passed in if the page isn't live.
  useEffect(function () {
    if (product !== 'confluence') {
      return;
    }
    if (dialogs && modalOptions) {
      dialogs.showDialog(ModalCustomUIRenderer, {
        extension: extensionInModal,
        extensionData: extensionDataInModal,
        localId: localId,
        onTearDown: handleModalTearDown,
        bridge: bridge,
        size: modalOptions.size,
        closeOnEscape: modalOptions.closeOnEscape,
        closeOnOverlayClick: modalOptions.closeOnOverlayClick
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalOptions, dialogs]);
  var iframeProps = {
    accountId: accountId,
    apolloClient: client,
    contextIds: contextIds,
    components: getThreeLOPrompt,
    coreData: coreData,
    customBridgeMethods: customBridgeMethods,
    extension: extension,
    extensionData: extensionData,
    extensionPayload: extensionPayload,
    extensionViewData: extensionViewData,
    getContextToken: getContextToken,
    isHidden: isIframeHidden,
    isResizable: isIframeResizable,
    locale: locale,
    timezone: timezone,
    loadingComponent: loadingComponent
  };
  if (error) {
    // Sandbox deprecation error
    if (error === SANDBOX_DEPRECATION_ERROR) {
      return /*#__PURE__*/React.createElement(ForgeDeprecationMessage, {
        extension: extension,
        client: client,
        error: error
      });
    }

    // Currently no other error types are handled
  }
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Iframe, _extends({}, iframeProps, {
    entryPoint: entryPoint,
    isResizable: isIframeResizable,
    bridge: _objectSpread(_objectSpread({}, bridge), {}, {
      openModal: function openModal(opts) {
        setModalOptions(opts.data);
        return true;
      }
    }, onModalCustomUIRendererClose ? {
      close: function close(payload) {
        var _bridge$close;
        onModalCustomUIRendererClose === null || onModalCustomUIRendererClose === void 0 || onModalCustomUIRendererClose();
        // This bridge.close reference is defined below in ModalCustomUIRenderer.
        // It's required to call the correct onCloseFromApp callback
        bridge === null || bridge === void 0 || (_bridge$close = bridge.close) === null || _bridge$close === void 0 || _bridge$close.call(bridge, payload);
        return true;
      }
    } : {}),
    height: height,
    width: width,
    isInModal: isInModal,
    ref: iframeRef,
    onLoad: onIframeLoad,
    setIsLoading: setIsLoading,
    onError: onError
  })), modalOptions ? /*#__PURE__*/React.createElement(ModalCustomUIRenderer, _extends({}, iframeProps, {
    product: product,
    getThreeLOPrompt: getThreeLOPrompt,
    dialogs: dialogs,
    client: client,
    extension: extensionInModal,
    extensionData: extensionDataInModal,
    localId: localId,
    onTearDown: handleModalTearDown,
    bridge: _objectSpread(_objectSpread({}, bridge), {}, {
      close: function close(payload) {
        onCloseFromApp === null || onCloseFromApp === void 0 || onCloseFromApp(payload.data);
        return true;
      }
    }),
    onClose: handleModalClose,
    size: modalOptions.size,
    closeOnEscape: modalOptions.closeOnEscape,
    closeOnOverlayClick: modalOptions.closeOnOverlayClick,
    modalStyles: modalStyles,
    modalProvider: modalProvider,
    getModalWidth: getModalWidth,
    isIframeResizable: isIframeResizable
  })) : null);
};
export var ModalCustomUIRenderer = function ModalCustomUIRenderer(_ref2) {
  var _ref3;
  var accountId = _ref2.accountId,
    bridge = _ref2.bridge,
    coreData = _ref2.coreData,
    client = _ref2.client,
    closeOnEscape = _ref2.closeOnEscape,
    closeOnOverlayClick = _ref2.closeOnOverlayClick,
    consentMessage = _ref2.consentMessage,
    contextIds = _ref2.contextIds,
    customBridgeMethods = _ref2.customBridgeMethods,
    dialogs = _ref2.dialogs,
    entryPoint = _ref2.entryPoint,
    extension = _ref2.extension,
    extensionData = _ref2.extensionData,
    extensionPayload = _ref2.extensionPayload,
    extensionViewData = _ref2.extensionViewData,
    getContextToken = _ref2.getContextToken,
    getThreeLOPrompt = _ref2.getThreeLOPrompt,
    _ref2$isIframeHidden = _ref2.isIframeHidden,
    isIframeHidden = _ref2$isIframeHidden === void 0 ? false : _ref2$isIframeHidden,
    _ref2$isIframeResizab = _ref2.isIframeResizable,
    isIframeResizable = _ref2$isIframeResizab === void 0 ? false : _ref2$isIframeResizab,
    loadingComponent = _ref2.loadingComponent,
    locale = _ref2.locale,
    localId = _ref2.localId,
    mentionProvider = _ref2.mentionProvider,
    modalExtension = _ref2.modalExtension,
    modalStyles = _ref2.modalStyles,
    ModalProvider = _ref2.modalProvider,
    _onClose = _ref2.onClose,
    onConsentModalClose = _ref2.onConsentModalClose,
    onConsentSuccess = _ref2.onConsentSuccess,
    onError = _ref2.onError,
    onForgeDocUpdated = _ref2.onForgeDocUpdated,
    onIframeLoad = _ref2.onIframeLoad,
    onTearDown = _ref2.onTearDown,
    product = _ref2.product,
    size = _ref2.size,
    timezone = _ref2.timezone,
    getModalWidth = _ref2.getModalWidth;
  var _useState5 = useState(true),
    _useState6 = _slicedToArray(_useState5, 2),
    isOpen = _useState6[0],
    setIsOpen = _useState6[1];
  var _useState7 = useState(false),
    _useState8 = _slicedToArray(_useState7, 2),
    isLoading = _useState8[0],
    setIsLoading = _useState8[1];

  // This is for preventing inline dialog apps from being closed by an esc key press when a
  // modal is open until https://product-fabric.atlassian.net/browse/DSP-3552 is resolved.
  useEffect(function () {
    if (product !== 'confluence' || (modalExtension === null || modalExtension === void 0 ? void 0 : modalExtension.type) === 'macroConfig') {
      return;
    }
    function dismissEscape(e) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        if (closeOnEscape) {
          _onClose === null || _onClose === void 0 || _onClose();
          setIsOpen(false);
        }
      }
    }
    if (isOpen) {
      document.addEventListener('keydown', dismissEscape, {
        capture: true
      });
    }
    return function () {
      return document.removeEventListener('keydown', dismissEscape, {
        capture: true
      });
    };
  }, [isOpen, closeOnEscape, product, _onClose, modalExtension === null || modalExtension === void 0 ? void 0 : modalExtension.type]);
  var modalSize = (_ref3 = size !== null && size !== void 0 ? size : extension.properties.viewportSize) !== null && _ref3 !== void 0 ? _ref3 : 'medium';
  var modalWidth = getModalWidth ? getModalWidth(size) : getAtlasKitModalWidth(modalSize);
  var _getModalDimensions = getModalDimensions(modalSize),
    modalHeight = _getModalDimensions.height;

  // If size is defined, iframe modal should not be resizable.
  // In JSM ONLY, iframe modal should ALWAYS be resizable.
  // Iframe should scale to 100% of the modal so that Select drop downs are not cut off
  var isJsmModule = extension.type.startsWith('jiraServiceManagement');
  var isModalResizable = isJsmModule || (size !== undefined ? false : isIframeResizable);
  var renderModal = function renderModal() {
    return /*#__PURE__*/React.createElement(Modal, {
      width: modalWidth
      // "max" size modal have a height value of 100%, which needs to be set in this parent component
      // all other modal sizes have height values that do not depend on the parent component
      // therefore their heights should be handled by the `ModalContentsWrapper` below
      ,
      height: modalSize === 'max' ? modalHeight : undefined,
      onClose: function onClose() {
        _onClose === null || _onClose === void 0 || _onClose();
        setIsOpen(false);
      },
      onCloseComplete: onTearDown,
      testId: "custom-ui-modal-dialog"
      // For custom macro editor, we only want shouldCloseOnOverlayClick and shouldCloseOnEscapePress to be true when the resource is loading.
      // When the resource has loaded, we want to disable this to prevent potential data loss if the overlay or escape key is clicked by accident.
      ,
      shouldCloseOnOverlayClick: (modalExtension === null || modalExtension === void 0 ? void 0 : modalExtension.type) === 'macroConfig' && !isLoading ? false : closeOnOverlayClick,
      shouldCloseOnEscapePress: (modalExtension === null || modalExtension === void 0 ? void 0 : modalExtension.type) === 'macroConfig' && !isLoading ? false : closeOnEscape
    }, /*#__PURE__*/React.createElement(ModalContentWrapper, {
      size: modalSize,
      styles: modalStyles,
      isResizable: isIframeResizable
    }, /*#__PURE__*/React.createElement(CustomUIRenderer, {
      product: product,
      coreData: coreData,
      getThreeLOPrompt: getThreeLOPrompt,
      dialogs: dialogs,
      accountId: accountId,
      client: client,
      contextIds: contextIds,
      extension: extension,
      extensionData: extensionData,
      extensionViewData: extensionViewData,
      bridge: bridge,
      customBridgeMethods: customBridgeMethods,
      consentMessage: consentMessage,
      entryPoint: entryPoint,
      getContextToken: getContextToken,
      height: "100%",
      isIframeHidden: isIframeHidden,
      isIframeResizable: isModalResizable,
      locale: locale,
      localId: localId,
      mentionProvider: mentionProvider,
      modalExtension: modalExtension,
      onConsentSuccess: onConsentSuccess,
      onForgeDocUpdated: onForgeDocUpdated,
      timezone: timezone,
      onConsentModalClose: onConsentModalClose,
      onError: onError,
      onIframeLoad: onIframeLoad,
      loadingComponent: loadingComponent,
      extensionPayload: extensionPayload,
      modalStyles: modalStyles,
      onModalCustomUIRendererClose: function onModalCustomUIRendererClose() {
        _onClose === null || _onClose === void 0 || _onClose();
        setIsOpen(false);
      },
      isInModal: true,
      setIsLoading: setIsLoading
    })));
  };
  if (ModalProvider) {
    return /*#__PURE__*/React.createElement(ModalTransition, null, isOpen && /*#__PURE__*/React.createElement(ModalProvider, null, renderModal()));
  }
  return /*#__PURE__*/React.createElement(ModalTransition, null, isOpen && renderModal());
};